import api from '../../../services/api';
import type { TFunction } from 'i18next';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';
import type { AllocationUnit } from '../../../utils/dimensionConversion';
import type { QuantityUnit } from '../../../utils/commercialQuantity';
import {
  type SupplierOrderManualLandedCostCategory,
  SupplierOrderDto,
  SupplierOrderDocumentDto,
  getSupplierOrderDisplayNumber,
  normalizeSupplierOrderDocument,
  normalizeSupplierOrderDocuments,
  normalizeSupplierOrder,
  normalizeSupplierOrders,
} from '../../../dto/supplierOrder';
import {
  normalizeSupplierOrderPZDocuments,
  type SupplierOrderPZDocumentSummaryDto,
} from '../../../dto/pzTransportCandidates';

export type { SupplierOrderDto, SupplierOrderDocumentDto };
export type { SupplierOrderPZDocumentSummaryDto };
export { getSupplierOrderDisplayNumber };

export interface SupplierOrderItemPayload {
  id?: string | null;
  productVariantId?: string | null;
  name: string;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  unit?: SupplierOrderLineUnit;
  purchaseQuantity?: number;
  purchaseUnit?: SupplierOrderLineUnit;
  conversionQuantity?: number;
  conversionUnit?: SupplierOrderAllocationUnit | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  currency?: string;
  unitCost?: number;
  description?: string | null;
}

export interface SupplierOrderLandedCostPayloadItem {
  category: SupplierOrderManualLandedCostCategory;
  amount: number;
  currency?: string;
  note?: string | null;
}

export interface SupplierOrderPayload {
  orderNumber?: string | null;
  supplierId: string;
  factoryId?: string | null;
  status?: string;
  statusNotes?: string | null;
  currency?: string;
  totalAmount?: number;
  landedCosts?: SupplierOrderLandedCostPayloadItem[];
  landedCostAllocationMethod?: 'QUANTITY';
  useManualNumbering?: boolean;
  manualOrderNumber?: string | null;
  clientOrderNumber?: string | null;
  producer?: string | null;
  factoryLocation?: string | null;
  deliveryLocation?: string | null;
  description?: string | null;
  supplierReference?: string | null;
  proformaNumber?: string | null;
  proformaDate?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  waybillNumber?: string | null;
  waybillDate?: string | null;
  orderDate?: string;
  expectedDelivery?: string | null;
  actualDelivery?: string | null;
  estimatedCompletionDate?: string | null;
  notes?: string | null;
  accountManagerId?: string | null;
  items?: SupplierOrderItemPayload[];
}

export type SupplierOrderAllocationUnit = AllocationUnit;
export type SupplierOrderLineUnit = QuantityUnit;
export function stripSupplierOrderItemPurchaseFields(item: SupplierOrderItemPayload): SupplierOrderItemPayload {
  const operationalItem: SupplierOrderItemPayload = { ...item };
  delete operationalItem.currency;
  delete operationalItem.unitCost;
  delete (operationalItem as SupplierOrderItemPayload & { totalCost?: number }).totalCost;
  return operationalItem;
}

export function stripSupplierOrderPurchaseFields(payload: SupplierOrderPayload): SupplierOrderPayload {
  const operationalPayload: SupplierOrderPayload = { ...payload };
  delete operationalPayload.currency;
  delete operationalPayload.totalAmount;
  delete operationalPayload.landedCosts;
  delete operationalPayload.landedCostAllocationMethod;

  if (Array.isArray(operationalPayload.items)) {
    operationalPayload.items = operationalPayload.items.map(stripSupplierOrderItemPurchaseFields);
  }

  return operationalPayload;
}

export async function fetchSupplierOrders(query: Record<string, string | undefined> = {}): Promise<SupplierOrderDto[]> {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });

  const response = await api.get(`/supplier-orders${params.toString() ? `?${params.toString()}` : ''}`);
  return normalizeSupplierOrders(response.data, 'supplierOrders.list');
}

/**
 * 2026-08-11: the PZ documents already issued off a supplier order — powers
 * the read-only "Dokumenty PZ" card on the ZD's Powiązania tab.
 */
export async function fetchSupplierOrderPZDocuments(
  supplierOrderId: string,
): Promise<SupplierOrderPZDocumentSummaryDto[]> {
  const response = await api.get(`/supplier-orders/${supplierOrderId}/pz-documents`);
  return normalizeSupplierOrderPZDocuments(response.data, 'supplierOrders.pzDocuments');
}

export async function fetchSupplierOrder(id: string): Promise<SupplierOrderDto> {
  const response = await api.get(`/supplier-orders/${id}`);
  return normalizeSupplierOrder(response.data, 'supplierOrders.detail');
}

/**
 * Consistency warnings (purchase price deviation) answer 409 with the shared operational-exception contract. Pass
 * `confirmation` from a page that has `modal`/`t`; a cancelled confirmation
 * returns `null` and nothing was saved.
 */
export interface SupplierOrderSaveConfirmation {
  modal: Parameters<typeof submitWithOperationalException>[2];
  t: TFunction;
}

export async function createSupplierOrder(
  payload: SupplierOrderPayload,
  confirmation?: SupplierOrderSaveConfirmation,
): Promise<SupplierOrderDto | null> {
  const response = confirmation
    ? await submitWithOperationalException(
      (body) => api.post('/supplier-orders', body),
      payload as unknown as Record<string, unknown>,
      confirmation.modal,
      confirmation.t,
    )
    : await api.post('/supplier-orders', payload);
  if (!response) return null;
  return normalizeSupplierOrder(response.data, 'supplierOrders.create');
}

export async function fetchSuggestedSupplierOrderNumber(orderDate?: string): Promise<string> {
  const response = await api.get('/supplier-orders/suggest-number', {
    params: orderDate ? { orderDate } : undefined,
  });
  return response.data.suggestedNumber;
}

export async function updateSupplierOrder(
  id: string,
  payload: SupplierOrderPayload,
  confirmation?: SupplierOrderSaveConfirmation,
): Promise<SupplierOrderDto | null> {
  const response = confirmation
    ? await submitWithOperationalException(
      (body) => api.put(`/supplier-orders/${id}`, body),
      payload as unknown as Record<string, unknown>,
      confirmation.modal,
      confirmation.t,
    )
    : await api.put(`/supplier-orders/${id}`, payload);
  if (!response) return null;
  return normalizeSupplierOrder(response.data, 'supplierOrders.update');
}

/**
 * WP12 (1.13): a ZD with uploaded supplier documents is deleted after a
 * confirmed warning listing the files (they are deleted with the ZD). The
 * confirmation travels in the DELETE body. Returns false when the operator
 * cancelled the confirmation (nothing was deleted).
 */
export async function deleteSupplierOrder(
  id: string,
  confirmation?: SupplierOrderSaveConfirmation,
): Promise<boolean> {
  if (!confirmation) {
    await api.delete(`/supplier-orders/${id}`);
    return true;
  }
  const response = await submitWithOperationalException(
    (body) => api.delete(`/supplier-orders/${id}`, { data: body }),
    {},
    confirmation.modal,
    confirmation.t,
  );
  return response !== null;
}

/** WP12 B1 "Anuluj i ukryj" for a ZD (confirmed SUPPLIER_ORDER_ARCHIVED). */
export async function archiveSupplierOrder(id: string, confirmation: SupplierOrderSaveConfirmation): Promise<boolean> {
  const response = await submitWithOperationalException(
    (body) => api.post(`/supplier-orders/${id}/archive`, body),
    {},
    confirmation.modal,
    confirmation.t,
  );
  return response !== null;
}

/** Admin only: restore an archived ZD. */
export async function unarchiveSupplierOrder(id: string): Promise<void> {
  await api.post(`/supplier-orders/${id}/unarchive`);
}

export async function fetchSupplierOrderDocuments(supplierOrderId: string): Promise<SupplierOrderDocumentDto[]> {
  const response = await api.get(`/supplier-orders/${supplierOrderId}/documents`);
  return normalizeSupplierOrderDocuments(response.data, 'supplierOrders.documents');
}

export async function createSupplierOrderDocument(supplierOrderId: string, values: Record<string, any>): Promise<SupplierOrderDocumentDto> {
  const payload = new FormData();
  payload.append('documentType', values.documentType || 'SUPPLIER_INVOICE');
  payload.append('number', values.number || '');
  payload.append('documentDate', values.documentDate?.format ? values.documentDate.format('YYYY-MM-DD') : values.documentDate || '');
  payload.append('amount', values.amount == null ? '' : String(values.amount));
  payload.append('currency', values.currency || 'PLN');
  payload.append('notes', values.notes || '');

  const file = values.document?.[0]?.originFileObj || values.document?.[0]?.file || null;
  if (file) {
    payload.append('document', file);
  }

  const response = await api.post(`/supplier-orders/${supplierOrderId}/documents`, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return normalizeSupplierOrderDocument(response.data, 'supplierOrders.document.create');
}

export async function deleteSupplierOrderDocument(documentId: string) {
  await api.delete(`/supplier-orders/documents/${documentId}`);
}

export async function downloadSupplierOrderDocumentFile(documentId: string): Promise<Blob> {
  const response = await api.get(`/supplier-orders/documents/${documentId}/download`, {
    responseType: 'blob',
  });
  return response.data;
}

export type SupplierOrderPdfConversionUnit = 'pcs' | 'm2' | 'm3';

export async function downloadSupplierOrderPdf(
  id: string,
  language: 'en' | 'pl',
  conversionUnit?: SupplierOrderPdfConversionUnit,
): Promise<Blob> {
  const params = new URLSearchParams({ lang: language });
  if (conversionUnit) {
    params.set('conversionUnit', conversionUnit);
  }

  const response = await api.get(`/supplier-orders/${id}/pdf?${params.toString()}`, {
    responseType: 'blob',
  });

  return response.data;
}

/**
 * Szkic ZD z potwierdzenia ZK (decision 2026-10-04 pkt 4, migration 197):
 * drafts waiting on "Szkice do przejrzenia" (ADMIN / OPERATIONS). Quantities
 * only — a draft has no supplier and no prices yet.
 */
export interface SupplierOrderConfirmationDraftItem {
  id: string;
  name: string;
  productVariantId: string | null;
  quantity: number | null;
  unit: string;
  width: number | string | null;
  height: number | string | null;
  length: number | string | null;
  thickness: number | string | null;
  productCategoryDimensionDisplayProfile: string | null;
}

export interface SupplierOrderConfirmationDraft {
  id: string;
  orderNumber: string;
  createdAt: string | null;
  confirmedByName: string | null;
  /** List projection; unlike SupplierOrderDto.sourceOrder, carries customerName, not notes. */
  sourceOrder: { id: string; orderNumber: string; customerName: string | null } | null;
  /** „Skąd realizujemy?” of the source ZK (read live) and the Handlowiec's message. */
  fulfillment: { source: 'WAREHOUSE' | 'FACTORY_DIRECT'; note: string | null };
  items: SupplierOrderConfirmationDraftItem[];
}

export function normalizeSupplierOrderConfirmationDrafts(raw: unknown): SupplierOrderConfirmationDraft[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row: any) => ({
    id: String(row?.id ?? ''),
    orderNumber: String(row?.orderNumber ?? ''),
    createdAt: typeof row?.createdAt === 'string' ? row.createdAt : null,
    confirmedByName: typeof row?.confirmedByName === 'string' ? row.confirmedByName : null,
    sourceOrder: row?.sourceOrder?.id
      ? {
        id: String(row.sourceOrder.id),
        orderNumber: String(row.sourceOrder.orderNumber ?? ''),
        customerName: typeof row.sourceOrder.customerName === 'string' ? row.sourceOrder.customerName : null,
      }
      : null,
    fulfillment: {
      source: row?.fulfillment?.source === 'FACTORY_DIRECT' ? ('FACTORY_DIRECT' as const) : ('WAREHOUSE' as const),
      note: typeof row?.fulfillment?.note === 'string' && row.fulfillment.note ? row.fulfillment.note : null,
    },
    items: Array.isArray(row?.items) ? row.items.map((item: any) => ({
      id: String(item?.id ?? ''),
      name: String(item?.name ?? ''),
      productVariantId: typeof item?.productVariantId === 'string' ? item.productVariantId : null,
      quantity: item?.quantity != null && Number.isFinite(Number(item.quantity)) ? Number(item.quantity) : null,
      unit: String(item?.unit ?? 'pcs'),
      width: item?.width ?? null,
      height: item?.height ?? null,
      length: item?.length ?? null,
      thickness: item?.thickness ?? null,
      productCategoryDimensionDisplayProfile: typeof item?.productCategoryDimensionDisplayProfile === 'string'
        ? item.productCategoryDimensionDisplayProfile
        : null,
    })) : [],
  })).filter((draft) => draft.id);
}

export async function fetchSupplierOrderConfirmationDrafts(): Promise<SupplierOrderConfirmationDraft[]> {
  const response = await api.get('/supplier-orders/confirmation-drafts');
  return normalizeSupplierOrderConfirmationDrafts(response.data);
}

export async function rejectSupplierOrderConfirmationDraft(id: string, reason: string): Promise<void> {
  await api.post(`/supplier-orders/${id}/reject-draft`, { reason });
}
